import { AsyncState, Updater, unloadedAsyncState } from 'widgets-for-react'
import { UserSubscriptions } from '../../../subscription/subscriptionState'
import {
  loadEmailKnown,
  loadPromoCode,
  loadSchools,
  loadSubscribeExistingUser,
  loadSubscribeNewUser,
} from './studentSubscriptionApi'
import { AuthState } from '../../../authentication/auth_widget/authState'

export type StudentSubscriptionState = {
  step: "enterSchool" | "checkCode"
  trySubscribe: boolean
  formInput: Partial<FormInput>
  showFormInputValidation: { [field in keyof FormInput]: boolean }
  schoolsState: AsyncState<SchoolsData>
  checkCodeState: AsyncState<PromoCodeResult>
  checkEmailState: AsyncState<boolean>
  subscribeNewUserState: AsyncState<NewUserStatus>
  subscribeExistingUserState: AsyncState<{}>
}

export type FormInput = {
  email: string
  schoolId: number
  code: string
  name: string
  surname: string
  graduationYear: number
  password: string
  showPassword: boolean
  marketingEmails: boolean
  termsAndConditions: boolean
}

export type SchoolsData = {
  Id: number
  Title: string
}[]

export type PromoCodeData = {
  EndDate: string
}[]

export type PromoCodeResult = {
  kind: "found"
  data: PromoCodeData 
} | {
  kind: "invalid code" 
} | {
  kind: "too many attempts"
} | { 
  kind: "error"
} 

export type NewUserStatus = {
  status: string
  session: string
  value: {
    id: string
    email: string
    username: string
  }
  role: string
}

export const initialStudentSubscriptionState = (
  userSubscriptionsState: AsyncState<UserSubscriptions>
): StudentSubscriptionState => {
  const formInput =
    userSubscriptionsState.kind == 'loaded'
      ? {
          email: userSubscriptionsState.value.User.Email,
          name: userSubscriptionsState.value.User.Name,
          surname: userSubscriptionsState.value.User.Surname,
        }
      : {}

  return {
    step: "enterSchool",
    trySubscribe: false,
    formInput: formInput,
    showFormInputValidation: {
      email: false,
      schoolId: false,
      code: false,
      name: false,
      surname: false,
      graduationYear: false,
      password: false,
      showPassword: false,
      marketingEmails: false,
      termsAndConditions: false,
    },
    schoolsState: loadSchools,
    checkCodeState: unloadedAsyncState(),
    checkEmailState: unloadedAsyncState(),
    subscribeNewUserState: unloadedAsyncState(),
    subscribeExistingUserState: unloadedAsyncState(),
  }
}

export const studentSubscriptionStateUpdaters = {
  updateSchoolsState:
    (updater: Updater<AsyncState<SchoolsData>>) =>
    (state: StudentSubscriptionState): StudentSubscriptionState => ({
      ...state,
      schoolsState: updater(state.schoolsState),
    }),
  updateCheckCodeState:
    (updater: Updater<AsyncState<PromoCodeResult>>) =>
    (state: StudentSubscriptionState): StudentSubscriptionState => ({
      ...state,
      checkCodeState: updater(state.checkCodeState),
    }),
  updateCheckEmailState:
    (updater: Updater<AsyncState<boolean>>) =>
    (state: StudentSubscriptionState): StudentSubscriptionState => ({
      ...state,
      checkEmailState: updater(state.checkEmailState),
    }),
  updateSubscribeNewUserState:
    (updater: Updater<AsyncState<NewUserStatus>>) =>
    (
      state: StudentSubscriptionState
    ): StudentSubscriptionState & { authStateUpdater?: Updater<AuthState> } => {
      const subscribeNewUserState = updater(state.subscribeNewUserState)
      return {
        ...state,
        subscribeNewUserState: subscribeNewUserState,
        authStateUpdater:
          subscribeNewUserState.kind === 'loaded' &&
          subscribeNewUserState.value.status === 'success'
            ? (authState: AuthState): AuthState => ({
                ...authState,
                status: 'logged in',
                user: subscribeNewUserState.value.value,
                session: subscribeNewUserState.value.session,
              })
            : undefined,
      }
    },
  updateSubscribeExistingUserState:
    (updater: Updater<AsyncState<{}>>) =>
    (state: StudentSubscriptionState): StudentSubscriptionState => ({
      ...state,
      subscribeExistingUserState: updater(state.subscribeExistingUserState),
    }),
  updateFormInput:
    (field: keyof FormInput, newValue: string | boolean | number) =>
    (state: StudentSubscriptionState): StudentSubscriptionState => ({
      ...state,
      formInput: {
        ...state.formInput,
        [field]: newValue,
      },
    }),
  showFormInputValition:
    (field: keyof FormInput) =>
    (state: StudentSubscriptionState): StudentSubscriptionState => ({
      ...state,
      showFormInputValidation: {
        ...state.showFormInputValidation,
        [field]: true,
      },
    }),
  checkCodeAndEmail:
    (isLoggedIn: boolean) =>
    (state: StudentSubscriptionState): StudentSubscriptionState => ({
      ...state,
      checkEmailState: isLoggedIn
        ? state.checkEmailState
        : loadEmailKnown(state.formInput.email || ''),
      checkCodeState: loadPromoCode(state.formInput.code || '', state.formInput.schoolId ?? -1),
    }),
  subscribeExistingUser: (
    state: StudentSubscriptionState
  ): StudentSubscriptionState => ({
    ...state,
    subscribeExistingUserState: loadSubscribeExistingUser(
      state.formInput.code || '',
      {
        Name: state.formInput.name || '',
        Surname: state.formInput.surname || '',
        ExpectedGraduationYear: state.formInput.graduationYear || NaN,
        SchoolId: state.formInput.schoolId || NaN,
      }
    ),
  }),
  subscribeNewUser: (
    state: StudentSubscriptionState
  ): StudentSubscriptionState => ({
    ...state,
    subscribeNewUserState: loadSubscribeNewUser(state.formInput.code || '', {
      Email: state.formInput.email || '',
      Password: state.formInput.password || '',
      Name: state.formInput.name || '',
      Surname: state.formInput.surname || '',
      MarketingEmails: state.formInput.marketingEmails || false,
      ExpectedGraduationYear: state.formInput.graduationYear || NaN,
      SchoolId: state.formInput.schoolId || NaN,
    }),
  }),
  progressStateToCheckCode: (
    state: StudentSubscriptionState
  ): StudentSubscriptionState => ({
    ...state,
    step: "checkCode"
  }),
  flagToSubscribe: (
    state: StudentSubscriptionState
  ): StudentSubscriptionState => ({
    ...state,
    subscribeExistingUserState: unloadedAsyncState(),
    subscribeNewUserState: unloadedAsyncState(),
    trySubscribe: true
  })
}
